import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { Watermark } from "./Watermark";

const REFS = [
  { label: "Standard door (36 in)", inches: 36 },
  { label: "Sofa (84 in)", inches: 84 },
  { label: "Queen bed (60 in)", inches: 60 },
  { label: "Letter paper (11 in)", inches: 11 },
  { label: "Light switch plate (4.5 in)", inches: 4.5 },
];

// Drags an item stored as fractional coordinates {x, y} within the container.
function useDrag(boxRef, setPos) {
  return (e) => {
    e.preventDefault();
    const rect = boxRef.current.getBoundingClientRect();
    const move = (ev) => setPos({
      x: Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height)),
    });
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
}

export const WallPhotoPreview = ({ src, title, w, h }) => {
  const boxRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [ref, setRef] = useState(REFS[0].inches);
  const [custom, setCustom] = useState("");
  const [a, setA] = useState({ x: 0.3, y: 0.75 });
  const [b, setB] = useState({ x: 0.5, y: 0.75 });
  const [art, setArt] = useState({ x: 0.5, y: 0.35 });
  const [boxW, setBoxW] = useState(1);
  const dragA = useDrag(boxRef, setA), dragB = useDrag(boxRef, setB), dragArt = useDrag(boxRef, setArt);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto(URL.createObjectURL(f));
  };
  const refInches = Number(custom) > 0 ? Number(custom) : ref;
  const onImgLoad = () => setBoxW(boxRef.current?.getBoundingClientRect().width || 1);
  const rect = boxRef.current?.getBoundingClientRect();
  const refPx = rect ? Math.hypot((b.x - a.x) * rect.width, (b.y - a.y) * rect.height) : 0;
  const pxPerIn = refPx / refInches;
  const artW = w * pxPerIn, artH = h * pxPerIn;

  if (!photo) return (
    <div data-testid="wall-photo-intro">
      <label className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-ink hover:bg-ink hover:text-cream transition-colors cursor-pointer" data-testid="wall-photo-upload">
        <Camera size={13}/> Try it on your wall
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="wall-photo-input" />
      </label>
      <p className="mt-3 text-sm text-ink-soft leading-relaxed max-w-md">
        See this print at its real size in your own space. Your photo stays on your device — nothing is uploaded.
      </p>
      <ol className="mt-3 space-y-1.5 text-sm text-ink-soft list-decimal pl-5" data-testid="wall-photo-steps">
        <li>Snap or choose a photo of the wall where the print would hang.</li>
        <li>Pick something in the photo with a known width (a door, sofa, or bed) and drag the gold line across it.</li>
        <li>The print appears at true scale for the size you selected — drag it anywhere on the wall.</li>
        <li>Change the size or material above and the print resizes instantly.</li>
      </ol>
    </div>
  );

  return (
    <div data-testid="wall-photo-preview">
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
        <span className="uppercase tracking-[0.18em] text-mute">Step 1 · Drag the gold line across a</span>
        <select value={ref} onChange={e => { setRef(Number(e.target.value)); setCustom(""); }} className="border border-line bg-transparent px-2 py-1 text-xs" data-testid="wall-ref-select">
          {REFS.map(r => <option key={r.inches} value={r.inches}>{r.label}</option>)}
        </select>
        <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="or custom in" type="number" min="1"
          className="w-28 border border-line bg-transparent px-2 py-1 text-xs" data-testid="wall-ref-custom" />
        <button onClick={() => setPhoto(null)} className="ml-auto inline-flex items-center gap-1 text-mute hover:text-ink uppercase tracking-[0.18em]" data-testid="wall-photo-clear"><X size={12}/> Remove</button>
      </div>
      <div ref={boxRef} className="relative w-full select-none touch-none overflow-hidden bg-black/5" data-testid="wall-photo-box">
        <img src={photo} alt="Your wall" className="w-full h-auto block" onLoad={onImgLoad} draggable={false} />
        {boxW > 1 && (
          <>
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <line x1={`${a.x * 100}%`} y1={`${a.y * 100}%`} x2={`${b.x * 100}%`} y2={`${b.y * 100}%`} stroke="#e8b04a" strokeWidth="3" strokeDasharray="6 4" />
            </svg>
            {[[a, dragA, "a"], [b, dragB, "b"]].map(([p, d, k]) => (
              <div key={k} onPointerDown={d} data-testid={`wall-ref-handle-${k}`}
                className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-[#e8b04a] border-2 border-white shadow cursor-grab active:cursor-grabbing"
                style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} />
            ))}
            <div className="absolute text-[10px] uppercase tracking-[0.18em] bg-black/60 text-white px-2 py-0.5 pointer-events-none"
              style={{ left: `${(a.x + b.x) / 2 * 100}%`, top: `${(a.y + b.y) / 2 * 100}%`, transform: "translate(-50%, 8px)" }}>{refInches} in</div>
            {pxPerIn > 0 && (
              <div onPointerDown={dragArt} data-testid="wall-artwork"
                className="absolute cursor-move shadow-[0_8px_24px_-6px_rgba(0,0,0,0.6)]"
                style={{ left: `${art.x * 100}%`, top: `${art.y * 100}%`, width: artW, height: artH, transform: "translate(-50%, -50%)" }}>
                <img src={src} alt={title} className="w-full h-full object-cover pointer-events-none" draggable={false} />
              </div>
            )}
          </>
        )}
        <Watermark />
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-mute" data-testid="wall-photo-hint">
        Step 2 · Print shown at {w}×{h} in — drag it anywhere on your wall
      </p>
    </div>
  );
};
